import React, { useState, useEffect, useRef } from 'react';
import { graphicAPI, categoryAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, showAdminAccessDialog, ADMIN_ACTIONS } from '../../utils/adminUtils';

import {
  Search,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Square,
  Circle,
  Triangle,
  Star,
  Heart,
  ArrowRight,
  MessageCircle,
  Hexagon,
  MessageSquare,
  GitBranch,
  Flag,
  Shapes,
  Sparkles,
  Filter
} from 'lucide-react';
import {
  ALL_SHAPES,
  SHAPE_CATEGORIES,
  getShapesByCategory,
  getAllCategories
} from '../../data/shapesData';

const ElementsSidebar = ({ isOpen, onClose, onElementSelect }) => {
  const { user } = useAuth();
  const [elements, setElements] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  // Default to 'all' so the panel shows the built-in Basic Shapes + shape
  // catalog immediately on open (rather than an empty DB category).
  const [selectedRootCategory, setSelectedRootCategory] = useState('all');
  const [selectedChildCategory, setSelectedChildCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#8B5CF6' });

  // Refs for horizontal scrolling
  const rootCategoriesRef = useRef(null);
  const childCategoriesRef = useRef(null);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || 'dev-token-' + Date.now();
  };

  // Fetch elements with pagination
  const fetchElements = async (resetElements = true, pageNum = 1) => {
    if (resetElements) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        page: pageNum,
        limit: 20,
      };

      // Add category filter
      if (selectedChildCategory !== 'all') {
        params.category_id = selectedChildCategory;
      } else if (selectedRootCategory && selectedRootCategory !== 'all') {
        params.category_id = selectedRootCategory;
      }

      // Add search filter
      if (searchQuery) {
        params.search = searchQuery;
      }

      const resp = await graphicAPI.getAll(params);
      const data = resp.data;

      if (data.success) {
        const elementsData = Array.isArray(data.data) ? data.data : data.data.graphics || [];

        if (resetElements) {
          setElements(elementsData);
        } else {
          setElements(prev => [...prev, ...elementsData]);
        }

        setHasMore(elementsData.length === params.limit);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching elements:', error);
      if (resetElements) {
        setElements([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch root categories (parentId is null)
  const fetchRootCategories = async () => {
    try {
      const response = await categoryAPI.getAll({ type: 'graphic', parentId: '' });
      const data = response.data;

      if (data.success) {
        const rootCategoriesData = data.data || [];
        // Sort root categories by name
        rootCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setRootCategories(rootCategoriesData);
        // Note: we intentionally keep the default 'all' selection so the panel
        // opens showing the built-in Basic Shapes + shape catalog, rather than
        // auto-jumping into the first (often empty) database category.
      }
    } catch (error) {
      console.error('Error fetching root categories:', error);
    }
  };

  // Fetch child categories for selected root category
  const fetchChildCategories = async (parentId) => {
    try {
      if (!parentId || parentId === 'all') {
        setChildCategories([]);
        return;
      }

      const response = await categoryAPI.getAll({ type: 'graphic', parentId });
      const data = response.data;

      if (data.success) {
        const childCategoriesData = data.data || [];
        // Sort child categories by name
        childCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChildCategories(childCategoriesData);
      }
    } catch (error) {
      console.error('Error fetching child categories:', error);
      setChildCategories([]);
    }
  };

  // Load more elements
  const loadMoreElements = () => {
    if (!loadingMore && hasMore) {
      fetchElements(false, page + 1);
    }
  };

  // Handle root category selection
  const handleRootCategorySelect = (category_id) => {
    setSelectedRootCategory(category_id);
    setSelectedChildCategory('all'); // Reset child category selection
    setPage(1); // Reset pagination

    // Fetch child categories for the selected root category
    if (category_id && category_id !== 'all') {
      fetchChildCategories(category_id);
    } else {
      setChildCategories([]);
    }
  };

  // Handle child category selection
  const handleChildCategorySelect = (category_id) => {
    setSelectedChildCategory(category_id);
    setPage(1); // Reset pagination
  };

  // Horizontal scroll functions
  const scrollCategories = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 200;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Create category
  const createCategory = async () => {
    // Check admin access
    if (!isAdmin(user)) {
      showAdminAccessDialog(ADMIN_ACTIONS.CREATE_CATEGORY);
      return;
    }

    try {
      await categoryAPI.create({ ...newCategory, type: 'graphic' });
      setNewCategory({ name: '', description: '', color: '#8B5CF6' });
      fetchRootCategories();

      // Show success notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'success',
          message: 'Elements category created successfully!'
        }
      }));
    } catch (error) {
      console.error('Error creating category:', error);

      // Show error notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'error',
          message: error.response?.data?.message || 'Failed to create category. Please try again.'
        }
      }));
    }
  };

  // Update category
  const updateCategory = async (id, data) => {
    try {
      await categoryAPI.update(id, data);
      setEditingCategory(null);
      fetchRootCategories();
      // Refresh child categories if we're viewing them
      if (selectedRootCategory && selectedRootCategory !== 'all') {
        fetchChildCategories(selectedRootCategory);
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoryAPI.delete(id);
        fetchRootCategories();
        // Refresh child categories if we're viewing them
        if (selectedRootCategory && selectedRootCategory !== 'all') {
          fetchChildCategories(selectedRootCategory);
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  // Built-in basic shapes
  const builtInShapes = [
    { id: 'rectangle', name: 'Rectangle', icon: Square, type: 'shape', shapeType: 'rectangle' },
    { id: 'square', name: 'Square', icon: Square, type: 'shape', shapeType: 'square' },
    { id: 'circle', name: 'Circle', icon: Circle, type: 'shape', shapeType: 'circle' },
    { id: 'triangle', name: 'Triangle', icon: Triangle, type: 'shape', shapeType: 'triangle' },
    { id: 'star', name: 'Star', icon: Star, type: 'shape', shapeType: 'star' },
    { id: 'heart', name: 'Heart', icon: Heart, type: 'shape', shapeType: 'heart' },
    { id: 'arrow', name: 'Arrow', icon: ArrowRight, type: 'shape', shapeType: 'arrow' },
    { id: 'hexagon', name: 'Hexagon', icon: Hexagon, type: 'shape', shapeType: 'hexagon' },
    { id: 'plus', name: 'Plus', icon: Plus, type: 'shape', shapeType: 'plus' },
    { id: 'chat', name: 'Chat Bubble', icon: MessageCircle, type: 'shape', shapeType: 'chat' },
  ];

  // Shape category icons
  const getCategoryIcon = (category) => {
    const icons = {
      [SHAPE_CATEGORIES.BASIC]: Square,
      [SHAPE_CATEGORIES.CALLOUTS]: MessageSquare,
      [SHAPE_CATEGORIES.FLOWCHART]: GitBranch,
      [SHAPE_CATEGORIES.BANNERS]: Flag,
      [SHAPE_CATEGORIES.GEOMETRIC]: Shapes,
      [SHAPE_CATEGORIES.DECORATIVE]: Sparkles
    };
    return icons[category] || Shapes;
  };

  // Get filtered shapes based on search and category
  const getFilteredShapes = () => {
    let shapes = ALL_SHAPES;

    if (selectedRootCategory !== 'all' && selectedRootCategory !== 'shapes') {
      // For now, we'll show all shapes when a database category is selected
      // In the future, this could be enhanced to filter by shape category
      shapes = ALL_SHAPES;
    }

    if (searchQuery) {
      shapes = shapes.filter(shape =>
        shape.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return shapes;
  };

  // Render shape preview using SVG path
  const renderShapePreview = (shape) => {
    return (
      <svg
        width="32"
        height="32"
        viewBox={shape.viewBox || "0 0 100 100"}
        className="text-gray-600 group-hover:text-purple-600"
      >
        <path
          d={shape.svgPath}
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    );
  };

  // Load data when sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchRootCategories();
    }
  }, [isOpen]);

  // Load child categories when root category changes
  useEffect(() => {
    if (selectedRootCategory && selectedRootCategory !== 'all') {
      fetchChildCategories(selectedRootCategory);
    } else {
      setChildCategories([]);
    }
  }, [selectedRootCategory]);

  // Load elements when filters change
  useEffect(() => {
    if (isOpen) {
      fetchElements(true, 1); // Reset elements and start from page 1
    }
  }, [isOpen, selectedRootCategory, selectedChildCategory, searchQuery]);

  if (!isOpen) return null;

  return (
    <>
      {/* Custom CSS for hiding scrollbars */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Elements</h2>
        </div>
        <button
          onClick={() => setShowCategoryManager(!showCategoryManager)}
          className="p-2 hover:bg-gray-100 rounded"
          title="Manage Categories"
        >
          <Edit className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search elements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">

      {/* Category Manager */}
      {showCategoryManager && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="font-medium mb-3">Manage Categories</h3>

          {/* Add New Category */}
          <div className="space-y-2 mb-4">
            <input
              type="text"
              placeholder="Category name"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={newCategory.description}
              onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
            <div className="flex space-x-2">
              <input
                type="color"
                value={newCategory.color}
                onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                className="w-10 h-8 border border-gray-300 rounded"
              />
              <button
                onClick={createCategory}
                disabled={!newCategory.name}
                className="flex-1 bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Add
              </button>
            </div>
          </div>

          {/* Existing Categories */}
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {rootCategories.map((category) => (
              <div key={category.id} className="flex items-center justify-between p-2 bg-white rounded border">
                {editingCategory === category.id ? (
                  <div className="flex-1 space-y-1">
                    <input
                      type="text"
                      defaultValue={category.name}
                      onBlur={(e) => updateCategory(category.id, { name: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 flex-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="text-sm">{category.name}</span>
                  </div>
                )}
                <div className="flex space-x-1">
                  <button
                    onClick={() => setEditingCategory(editingCategory === category.id ? null : category.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => deleteCategory(category.id)}
                    className="p-1 hover:bg-gray-100 rounded text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two-Line Category System */}
      <div className="border-b border-gray-200 bg-gray-50">
        {/* First Line - Root Categories */}
        <div className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => scrollCategories(rootCategoriesRef, 'left')}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
              title="Scroll left"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <div
              ref={rootCategoriesRef}
              className="flex space-x-2 overflow-x-auto scrollbar-hide flex-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <button
                onClick={() => handleRootCategorySelect('all')}
                className={`px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
                  selectedRootCategory === 'all' || !selectedRootCategory
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                All Categories
              </button>
              <button
                onClick={() => handleRootCategorySelect('shapes')}
                className={`px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
                  selectedRootCategory === 'shapes'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                Basic Shapes
              </button>
              {rootCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleRootCategorySelect(category.id)}
                  className={`px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 flex items-center space-x-1 transition-colors ${
                    selectedRootCategory === category.id
                      ? 'text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  style={{
                    backgroundColor: selectedRootCategory === category.id ? category.color || '#8B5CF6' : undefined,
                  }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: selectedRootCategory === category.id
                        ? 'white'
                        : category.color || '#8B5CF6'
                    }}
                  />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => scrollCategories(rootCategoriesRef, 'right')}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
              title="Scroll right"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Second Line - Child Categories */}
        <div className="px-4 pb-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => scrollCategories(childCategoriesRef, 'left')}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
              title="Scroll left"
            >
              <ChevronLeft className="w-3 h-3 text-gray-500" />
            </button>
            <div
              ref={childCategoriesRef}
              className="flex space-x-2 overflow-x-auto scrollbar-hide flex-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <button
                onClick={() => handleChildCategorySelect('all')}
                className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
                  selectedChildCategory === 'all'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                All
              </button>
              {childCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleChildCategorySelect(category.id)}
                  className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap flex-shrink-0 flex items-center space-x-1 transition-colors ${
                    selectedChildCategory === category.id
                      ? 'text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                  style={{
                    backgroundColor: selectedChildCategory === category.id ? category.color || '#10B981' : undefined,
                  }}
                >
                  <div
                    className="w-1 h-1 rounded-full"
                    style={{
                      backgroundColor: selectedChildCategory === category.id
                        ? 'white'
                        : category.color || '#10B981'
                    }}
                  />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => scrollCategories(childCategoriesRef, 'right')}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
              title="Scroll right"
            >
              <ChevronRight className="w-3 h-3 text-gray-500" />
            </button>
          </div>
        </div>
      </div>

        {/* Elements Grid */}
        <div className="p-4">
        {/* Built-in Basic Shapes */}
        {(selectedRootCategory === 'all' || selectedRootCategory === 'shapes') && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Shapes</h3>
            <div className="grid grid-cols-3 gap-3">
              {builtInShapes.map((shape) => {
                const IconComponent = shape.icon;
                return (
                  <div
                    key={shape.id}
                    onClick={() => onElementSelect(shape)}
                    className="cursor-pointer group"
                  >
                    <div className="aspect-square bg-gray-50 rounded-lg border-2 border-gray-200 flex items-center justify-center group-hover:border-purple-300 group-hover:bg-purple-50 transition-colors">
                      <IconComponent className="w-8 h-8 text-gray-600 group-hover:text-purple-600" />
                    </div>
                    <p className="text-xs text-center mt-1 text-gray-600">{shape.name}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Comprehensive Shapes by Category */}
        {(selectedRootCategory === 'all' || selectedRootCategory === 'shapes') && (
          <div className="mb-6">
            {selectedRootCategory === 'all' ? (
              // Show all categories
              getAllCategories().map((category) => {
                const categoryShapes = getShapesByCategory(category);
                const IconComponent = getCategoryIcon(category);
                const categoryName = category.charAt(0).toUpperCase() + category.slice(1);

                return (
                  <div key={category} className="mb-8">
                    <div className="flex items-center space-x-2 mb-3">
                      <IconComponent className="w-4 h-4 text-gray-600" />
                      <h3 className="text-sm font-medium text-gray-700">{categoryName} Shapes</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {categoryShapes.map((shape) => (
                        <div
                          key={shape.id}
                          onClick={() => onElementSelect({ ...shape, type: 'shape' })}
                          className="cursor-pointer group"
                        >
                          <div className="aspect-square bg-gray-50 rounded-lg border-2 border-gray-200 flex items-center justify-center group-hover:border-purple-300 group-hover:bg-purple-50 transition-colors">
                            {renderShapePreview(shape)}
                          </div>
                          <p className="text-xs text-center mt-1 text-gray-600">{shape.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              // Show specific category
              <div>
                <div className="grid grid-cols-3 gap-3">
                  {getFilteredShapes().map((shape) => (
                    <div
                      key={shape.id}
                      onClick={() => onElementSelect({ ...shape, type: 'shape' })}
                      className="cursor-pointer group"
                    >
                      <div className="aspect-square bg-gray-50 rounded-lg border-2 border-gray-200 flex items-center justify-center group-hover:border-purple-300 group-hover:bg-purple-50 transition-colors">
                        {renderShapePreview(shape)}
                      </div>
                      <p className="text-xs text-center mt-1 text-gray-600">{shape.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Database Elements */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              {elements.map((element) => (
                <div
                  key={element.id}
                  onClick={() => onElementSelect(element)}
                  className="cursor-pointer group"
                >
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-2 group-hover:shadow-md transition-shadow">
                    {element.thumbnailUrl ? (
                      <img
                        src={element.thumbnailUrl}
                        alt={element.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-xs">No Preview</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 truncate">{element.name}</h3>
                  {element.isPremium && (
                    <span className="text-xs text-yellow-600">Premium</span>
                  )}
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && elements.length > 0 && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMoreElements}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 mx-auto"
                >
                  {loadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Load More Elements</span>
                  )}
                </button>
              </div>
            )}
          </>
        )}

          {!loading && elements.length === 0 && selectedRootCategory !== 'all' && selectedRootCategory !== 'shapes' && (
            <div className="text-center text-gray-500 mt-8">
              <p>No elements found</p>
              {searchQuery && (
                <p className="text-sm mt-1">Try adjusting your search or category filter</p>
              )}
              {selectedChildCategory !== 'all' && (
                <p className="text-sm mt-1">Try selecting a different category</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ElementsSidebar;
